import { useState } from 'react'
import { lumi } from '@/lib/lumi'

export interface DiscountRule {
  _id?: string
  minDays: number
  discountPercent: number
  label: string
  active: boolean
  creator: string
  createdAt: string
  updatedAt: string
}

export function useDiscountRules() {
  const [rules, setRules] = useState<DiscountRule[]>([])
  const [loading, setLoading] = useState(false)

  const fetchRules = async () => {
    setLoading(true)
    try {
      const { list } = await lumi.entities.discount_rules.list({
        sort: { minDays: 1 },
        limit: 50,
      })
      setRules(list || [])
    } catch (error) {
      console.error('Failed to fetch discount rules:', error)
    } finally {
      setLoading(false)
    }
  }

  const createRule = async (data: Omit<DiscountRule, '_id'>) => {
    const result = await lumi.entities.discount_rules.create(data)
    await fetchRules()
    return result
  }

  const updateRule = async (id: string, data: Partial<DiscountRule>) => {
    const result = await lumi.entities.discount_rules.update(id, data)
    await fetchRules()
    return result
  }

  const deleteRule = async (id: string) => {
    await lumi.entities.discount_rules.delete(id)
    await fetchRules()
  }

  /**
   * Trouve le meilleur rabais applicable pour un nombre de jours donne
   * Retourne le rabais avec le plus grand minDays qui est <= numberOfDays
   */
  const getApplicableDiscount = (numberOfDays: number): DiscountRule | null => {
    const activeRules = rules
      .filter((r) => r.active !== false && r.minDays <= numberOfDays)
      .sort((a, b) => b.minDays - a.minDays)
    return activeRules.length > 0 ? activeRules[0] : null
  }

  return { rules, loading, fetchRules, createRule, updateRule, deleteRule, getApplicableDiscount }
}
